import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { getCommunicationTemplates, sendCommunication } from '../../api/communicationTemplates';
import { userList } from '../../api/user';

const SendSMS = () => {
  // Company selection logic
  let companyOptions = [];
  const companiesStr = localStorage.getItem('companies');
  if (companiesStr) {
    try {
      const companies = JSON.parse(companiesStr);
      if (Array.isArray(companies) && companies.length > 0) {
        companyOptions = [
          ...companies.map(c => ({
            value: c.id,
            label: c.company_name
          }))
        ];
      }
    } catch (e) {
      companyOptions = [];
    }
  }
  const [selectedCompany, setSelectedCompany] = useState('');

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [smsContent, setSmsContent] = useState('');
  
  const [recipientType, setRecipientType] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  
  // Test SMS functionality
  const [showTestSMSModal, setShowTestSMSModal] = useState(false);
  const [testMobileNumber, setTestMobileNumber] = useState('');
  const [sendingTestSMS, setSendingTestSMS] = useState(false);

  const recipientOptions = [
    { value: 'individual', label: 'Individual Users' },
    { value: 'active_users', label: 'Active Users' },
    { value: 'trial_users', label: 'Trial Users' },
    { value: 'inactive_users', label: 'Inactive Users' },
    { value: 'users_with_bookings', label: 'Users with Bookings' },
    { value: 'users_no_bookings', label: 'Users with No Current Bookings' },
  ];

  const userOptions = users.map(user => ({
    value: user.id,
    label: `${user.first_name || ''} ${user.last_name || ''} (${user.email})`.trim()
  }));

  const selectedUserOptions = userOptions.filter(option => selectedUsers.includes(option.value));

  useEffect(() => {
    // Only fetch if either no company options (not super admin) or company is selected
    if (companyOptions.length === 0 || selectedCompany) {
      fetchSMSTemplates();
      fetchUsers();
    }
  }, [selectedCompany]);

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const payload = { page: 1, limit: 10000 };
      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }
      const response = await userList(payload);
      if (response.code === 1 && response.data) {
        setUsers(response.data.user_list || response.data || []);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchSMSTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = { template_type: 'sms' };
      // Always pass company_id - either from selectedCompany or empty string
      payload.company_id = selectedCompany ? selectedCompany.value : '';
      const response = await getCommunicationTemplates(payload);
      if (response.code) {
        // Flatten the hierarchical structure to get all templates
        const flattenTemplates = (items) => {
          let result = [];
          items.forEach(item => {
            if (item.type === 'template') {
              result.push({
                id: item.id,
                name: item.name,
                content: item.content
              });
            }
            if (item.subfolders && item.subfolders.length > 0) {
              result = result.concat(flattenTemplates(item.subfolders));
            }
          });
          return result;
        };
        const allTemplates = flattenTemplates(response.data);
        setTemplates(allTemplates);
      } else {
        setError(response.message || 'Failed to fetch templates');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Error fetching templates');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (e) => {
    const templateId = parseInt(e.target.value);
    const template = templates.find(t => t.id === templateId);
    
    if (template) {
      setSelectedTemplate(template);
      setSmsContent(template.content || '');
    }
  };

  const handleUserSelection = (selectedOptions) => {
    const selectedIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setSelectedUsers(selectedIds);
  };

  const handleSendSMS = async () => {
    // Validation
    if (!selectedTemplate) {
      setError('Please select an SMS template');
      return;
    }
    if (!recipientType) {
      setError('Please select recipient type');
      return;
    }
    if (recipientType === 'individual' && selectedUsers.length === 0) {
      setError('Please select at least one user');
      return;
    }

    setSending(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await sendCommunication({
        template_type: 'sms',
        content: smsContent,
        recipient_type: recipientType,
        recipient_ids: selectedUsers
      });

      if (response.code === 1) {
        setSuccess(`SMS sent successfully to ${response.data.total_users} users. Success: ${response.data.success_count}, Failed: ${response.data.failure_count}`);
        // Reset form
        setSelectedTemplate(null);
        setSmsContent('');
        setRecipientType('');
        setSelectedUsers([]);
      } else {
        setError(response.message || 'Failed to send SMS');
      }
    } catch (err) {
      console.error('Error sending SMS:', err);
      setError('Error sending SMS');
    } finally {
      setSending(false);
    }
  };

  const handleSendTestSMS = async () => {
    // Validation
    if (!selectedTemplate) {
      setError('Please select an SMS template');
      return;
    }
    if (!testMobileNumber) {
      setError('Please enter a mobile number');
      return;
    }

    setSendingTestSMS(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await sendCommunication({
        template_type: 'sms',
        content: smsContent,
        test_email: testMobileNumber,
        is_test: true
      });

      if (response.code === 1) {
        setSuccess(`Test SMS sent successfully to ${testMobileNumber}`);
        setShowTestSMSModal(false);
        setTestMobileNumber('');
      } else {
        setError(response.message || 'Failed to send test SMS');
      }
    } catch (err) {
      console.error('Error sending test SMS:', err);
      setError('Error sending test SMS');
    } finally {
      setSendingTestSMS(false);
    }
  };

  return (
    <article className="col-lg-9 articleClass">
      <div className="article-body store-bd-gy">
        <div className="row align-items-center mb-4">
          <div className="col-sm-12">
            <h3 className="page-title">Send SMS</h3>
            <p className="text-muted">Send SMS to users using SMS templates</p>
          </div>
        </div>

        {/* Company Selection - Only show if there are company options */}
        {companyOptions.length > 0 && (
          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label">Select Company</label>
              <Select
                options={companyOptions}
                value={selectedCompany}
                onChange={(selected) => {
                  setSelectedCompany(selected);
                }}
                placeholder="Select Company"
              />
            </div>
          </div>
        )}

        {/* Show company selection message if no company is selected */}
        {companyOptions.length > 0 && !selectedCompany && (
          <div className="alert alert-info mb-4">
            Please select a company to send SMS.
          </div>
        )}

        <div className="card">
          <div className="card-body">
            {error && (
              <div className="alert alert-danger mb-3">
                {error}
                <button type="button" className="btn-close float-end" onClick={() => setError(null)}></button>
              </div>
            )}
            {success && (
              <div className="alert alert-success mb-3">
                {success}
                <button type="button" className="btn-close float-end" onClick={() => setSuccess(null)}></button>
              </div>
            )}

            {/* Show content only if no company options (not super admin) or company is selected */}
            {companyOptions.length > 0 && !selectedCompany ? (
              null
            ) : (
            <>

            <div className="row">
              {/* Template Selection */}
              <div className="col-md-6 mb-4">
                <label className="form-label fw-bold">
                  Select SMS Template <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={selectedTemplate?.id || ''}
                  onChange={handleTemplateChange}
                >
                  <option value="">Select Template</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Recipient Type Selection */}
              <div className="col-md-6 mb-4">
                <label className="form-label fw-bold">
                  Recipient Type <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={recipientType}
                  onChange={(e) => setRecipientType(e.target.value)}
                >
                  <option value="">Select Recipient Type</option>
                  {recipientOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Individual User Selection */}
              {recipientType === 'individual' && (
                <div className="col-md-12 mb-4">
                  <label className="form-label fw-bold">
                    Select Users <span className="text-danger">*</span>
                  </label>
                  <Select
                    isMulti
                    options={userOptions}
                    value={selectedUserOptions}
                    onChange={handleUserSelection}
                    placeholder="Search and select users..."
                    isSearchable
                    isClearable
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>
              )}

              {/* SMS Content */}
              <div className="col-md-12 mb-4">
                <label className="form-label fw-bold">
                  SMS Content <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control"
                  value={smsContent}
                  onChange={(e) => setSmsContent(e.target.value)}
                  placeholder="Enter SMS content"
                  rows="5"
                  style={{ minHeight: '150px', resize: 'vertical' }}
                />
                <small className="text-muted">
                  Character count: {smsContent.length}
                </small>
              </div>

              {/* Send Button */}
              <div className="col-md-12 d-flex gap-2">
                <button
                  className="btn btn-primary"
                  onClick={handleSendSMS}
                  disabled={sending}
                >
                  {sending ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Sending...
                    </>
                  ) : (
                    'Send SMS'
                  )}
                </button>
                <button
                  className="btn btn-info"
                  onClick={() => setShowTestSMSModal(true)}
                  disabled={sending}
                >
                  Send Test SMS
                </button>
              </div>
            </div>
            </>
            )}
          </div>
        </div>
      </div>

      {/* Test SMS Modal */}
      {showTestSMSModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Send Test SMS</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowTestSMSModal(false);
                    setTestMobileNumber('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Mobile Number <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={testMobileNumber}
                    onChange={(e) => setTestMobileNumber(e.target.value)}
                    placeholder="Enter mobile number"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowTestSMSModal(false);
                    setTestMobileNumber('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSendTestSMS}
                  disabled={sendingTestSMS}
                >
                  {sendingTestSMS ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Sending...
                    </>
                  ) : (
                    'Send Test SMS'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

export default SendSMS;
